import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, CheckIcon } from 'lucide-react';
import { getProduct, products } from '../data/products';
import { getProgram } from '../data/programs';
import { Button } from '../components/ui/Button';
import { Eyebrow } from '../components/ui/Eyebrow';
import { ProductCard } from '../components/shop/ProductCard';
import { CtaBand } from '../components/ui/CtaBand';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
export function ProductDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const product = slug ? getProduct(slug) : undefined;
  useDocumentMeta({
    title: product ? `${product.name} — Republic Pharmacy` : 'Pharmacy — Republic',
    description: product ? `${product.tagline} ${product.price} ${product.cadence} — ${product.priceNote}.` : 'FDA-approved medications and US-compounded peptides, priced in public.',
    image: product?.image
  });
  if (!product) {
    return <main className="bg-cream" data-id="element-456" data-mp="3jwai31hkqaj6">
        <div className="mx-auto max-w-shell px-6 py-32 md:px-8" data-id="element-457" data-mp="3juzhtbnc7kwt">
          <Eyebrow className="text-brick" data-id="element-458" data-mp="3oniqpvb2xrva">Not found</Eyebrow>
          <h1 className="mt-4 max-w-2xl font-serif text-4xl leading-tight text-ink md:text-6xl" data-id="element-459" data-mp="3op3pjx4ffwyy">
            We could not find that medication.
          </h1>
          <Link to="/shop" className="mt-8 inline-flex items-center gap-2 text-sm text-brick hover:text-ink" data-id="element-460" data-mp="3fvqmw3gk9gzo">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-461" data-mp="bgqrybc7988c" />
            Back to the pharmacy
          </Link>
        </div>
      </main>;
  }
  const program = getProgram(product.program);
  const related = products.filter(item => item.slug !== product.slug).slice(0, 3);
  return <main data-id="element-462" data-mp="2e7uy7v3hpu0">
      <section className="bg-cream" data-id="element-463" data-mp="2hvnb3ot0675">
        <div className="mx-auto max-w-shell px-6 pb-20 pt-12 md:px-8 md:pb-24 md:pt-16" data-id="element-464" data-mp="2i6xbiw56xd65">
          <Link to="/shop" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-150 ease-republic hover:text-ink" data-id="element-465" data-mp="2n6t3fx0m5e5c">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-466" data-mp="1z2tuni2w8s9t" />
            Pharmacy
          </Link>

          <div className="mt-10 grid gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16" data-id="element-467" data-mp="1ubvbmaqitg2v">
            <img src={product.image} alt={`${product.name}, ${product.form}`} className="aspect-square w-full bg-stone-light object-cover" data-id="element-468" data-mp="1u9qwewn68una" />

            <div data-id="element-469" data-mp="1kbkyeq4hk8np">
              <Eyebrow className={product.category === 'FDA-approved' ? 'text-brick' : 'text-ink-muted'} data-id="element-470" data-mp="1fekrye3q53w2">
                {product.status}
              </Eyebrow>
              <h1 className="mt-4 font-serif text-[2.75rem] leading-[1.02] text-ink md:text-[4rem]" data-id="element-471" data-mp="1bdiy6csktk1h">
                {product.name}
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-ink-soft" data-id="element-472" data-mp="35rjyz37osh47">
                {product.description}
              </p>

              <div className="rule mt-10 pt-8" data-id="element-473" data-mp="30sgsdec262u4">
                <div className="flex flex-wrap items-end justify-between gap-6" data-id="element-474" data-mp="38bq258oh83jp">
                  <div data-id="element-475" data-mp="389lzeu2bdal0">
                    <p className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-476" data-mp="3da9mx5rc39ah">
                      Published price
                    </p>
                    <p className="mt-3 font-serif text-5xl leading-none text-ink" data-id="element-477" data-mp="3i21glp5sdv4u">
                      ${product.price}
                      <span className="ml-3 font-sans text-sm text-ink-muted" data-id="element-478" data-mp="3n25s10ru7zo7">
                        {product.cadence}
                      </span>
                    </p>
                    <p className="mt-3 text-sm text-ink-muted" data-id="element-479" data-mp="2ubxghrrt4dil">{product.priceNote}</p>
                  </div>
                  <div className="text-sm text-ink-muted" data-id="element-480" data-mp="2zcuvjvhisrfv">
                    <p data-id="element-481" data-mp="33deqqtij9kzd">+ $39/mo Republic membership</p>
                    <p className="mt-1 text-ink" data-id="element-482" data-mp="ekjbhphef284">Rate held for 12 months</p>
                  </div>
                </div>

                <div className="mt-8 flex flex-wrap gap-4" data-id="element-483" data-mp="illcaaqyrmc7">
                  <Button to="/get-started" data-id="element-484" data-mp="io7ia5y7htsb">
                    Request this medication
                    <ArrowRightIcon className="h-4 w-4" data-id="element-485" data-mp="h57ipdr3e763" />
                  </Button>
                  {program && <Button to={`/programs/${program.slug}`} variant="secondary" data-id="element-486" data-mp="c88sv7egomdp">
                      See the {program.name} program
                    </Button>}
                </div>
                <p className="mt-5 max-w-lg text-xs leading-relaxed text-ink-muted" data-id="element-487" data-mp="m6eoj79ocwa4">
                  Prescription required. Requesting a medication starts a clinical review — you are
                  not charged until a clinician approves a protocol and you accept it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-488" data-mp="14vbmbl4a66x3">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-489" data-mp="1iu1i8xfd60jv">
          <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20" data-id="element-490" data-mp="1iryjnp05az3j">
            <div data-id="element-491" data-mp="1eukec8fl5bgt">
              <h2 className="font-serif text-3xl leading-tight text-ink md:text-4xl" data-id="element-492" data-mp="1eqwhp0eug8xv">
                What you are getting
              </h2>
              <ul className="mt-8 flex flex-col gap-4" data-id="element-493" data-mp="22gcwxe90841p">
                {product.highlights.map(highlight => <li key={highlight} className="flex items-start gap-3" data-id="element-494" data-mp="1ofjwydzxogr7">
                    <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-brick" strokeWidth={2} data-id="element-495" data-mp="1tewsro8s8y5x" />
                    <span className="text-base leading-relaxed text-ink-soft" data-id="element-496" data-mp="1eq0cx50higur">{highlight}</span>
                  </li>)}
              </ul>
            </div>

            <div data-id="element-497" data-mp="1jj379fd35uc0">
              <h2 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-498" data-mp="15q3sxgp1i661">
                Dispensing detail
              </h2>
              <dl className="mt-6" data-id="element-499" data-mp="19ryvspmmcrlx">
                <div className="rule flex items-baseline justify-between gap-6 py-4" data-id="element-500" data-mp="19p30l925oh16">
                  <dt className="text-sm text-ink-muted" data-id="element-501" data-mp="vtr1kpgyw4eg">Pharmacy</dt>
                  <dd className="text-right text-sm text-ink" data-id="element-502" data-mp="10pfx8hpv915h">{product.pharmacy}</dd>
                </div>
                {product.facts.map(fact => <div key={fact.label} className="rule flex items-baseline justify-between gap-6 py-4" data-id="element-503" data-mp="2xpggtiecxf7u">
                    <dt className="text-sm text-ink-muted" data-id="element-504" data-mp="2tn3bgv3ivjbl">{fact.label}</dt>
                    <dd className="max-w-[60%] text-right text-sm text-ink" data-id="element-505" data-mp="2tl8n16t1l92r">{fact.value}</dd>
                  </div>)}
                <div className="rule flex items-baseline justify-between gap-6 py-4" data-id="element-506" data-mp="3lf2dnz79bico">
                  <dt className="text-sm text-ink-muted" data-id="element-507" data-mp="3hcnvlhbewmmg">Prescription</dt>
                  <dd className="text-right text-sm text-ink" data-id="element-508" data-mp="3hbctdlxm7mxg">
                    {product.rxRequired ? 'Required' : 'Not required'}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-cream" data-id="element-509" data-mp="20zqpam19xdit">
        <div className="mx-auto max-w-shell px-6 py-16 md:px-8 md:py-20" data-id="element-510" data-mp="20wkz13gws5li">
          <h2 className="text-[0.6875rem] uppercase tracking-label text-ink-muted" data-id="element-511" data-mp="1vy9rize3676g">
            Also in the pharmacy
          </h2>
          <ul className="mt-8 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-id="element-512" data-mp="1qv0hhzj6vf6o">
            {related.map(item => <ProductCard key={item.slug} product={item} data-id="element-513" data-mp="1vude3bzv40mg" />)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-514" data-mp="20l26ctjd91mn" />
    </main>;
}